import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { openMemberType } from "util/openMemberType";
import { ROLE_LABELS } from "common/activeTypeGate";
import useBodyScrollLock from "hooks/useBodyScrollLock";
import RoleChangeModal from "components/ui/RoleChangeModal";
import { roleGateCopy } from "common/roleGateCopy";

/**
 * 활성유형 게이트 호스트 — API 인터셉터가 띄우는 전역 모달 2종.
 *  1) SWITCH_REQUIRED: "현재 일반 유형입니다 … 전환하고 계속하기" → 전환 후 원요청 자동 재시도
 *  2) ROLE_REQUIRED: 역할 미보유 → 회원유형 추가/인증 화면 유도
 *  App 루트(BrowserRouter 내부)에 1회 마운트.
 */
export default function ActiveTypeGateHost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeType, setActiveType } = useAuth();
  const [switchReq, setSwitchReq] = useState(null); // { body, resolve }
  const [roleReq, setRoleReq] = useState(null); // { body }
  useBodyScrollLock(!!switchReq || !!roleReq);

  useEffect(() => {
    // 대타공고 상세는 지원 실패 사유를 화면 자체 팝업(popupMsg)으로 보여준다 → 전역 팝업 중복 방지.
    //  ⚠️ 종전엔 여기에 "캐스팅 상세 + 활동유형 TEAM" 예외가 하나 더 있었다. 그 예외는
    //     ConfirmModal 이 숨김 상태에서도 body 를 렌더해 ResumePicker 가 페이지 진입 즉시
    //     GET /resumes 를 쏘던 버그를 가리는 반창고였고, 공연팀만 봐줘서 학원·원장에게는
    //     그대로 "강사·무용수 유형에서만" 팝업이 떴다. 원인(ConfirmModal)을 고치고 예외는 지운다.
    //     화면별 URL 예외를 늘리면 같은 구멍이 유형 수만큼 다시 생긴다.
    const isSubDetail = () => /^\/subs\/\d+\/?$/.test(window.location.pathname);
    const onSwitch = (e) => {
      e.detail.claimed = true; // 처리자 존재 표시(게이트 폴백 방지)
      if (isSubDetail()) {
        e.detail.resolve?.(false);
        return;
      }
      setSwitchReq(e.detail);
    };
    const onRole = (e) => {
      if (isSubDetail()) return;
      setRoleReq(e.detail);
    };
    const onDismiss = () => {
      setSwitchReq((current) => {
        current?.resolve?.(false);
        return null;
      });
      setRoleReq(null);
    };
    window.addEventListener("muyeon:switch-required", onSwitch);
    window.addEventListener("muyeon:role-required", onRole);
    window.addEventListener("muyeon:dismiss-role-gates", onDismiss);
    return () => {
      window.removeEventListener("muyeon:switch-required", onSwitch);
      window.removeEventListener("muyeon:role-required", onRole);
      window.removeEventListener("muyeon:dismiss-role-gates", onDismiss);
    };
  }, [activeType]);

  const suggested = switchReq?.body?.data?.suggested || null;
  const switchRoles = (switchReq?.body?.data?.required || []).filter((role) =>
    (switchReq?.body?.data?.held || []).includes(role));
  if (!switchRoles.length && suggested) switchRoles.push(suggested);

  const confirmSwitch = (role) => {
    if (role) setActiveType(role); // 로컬 + 서버(PATCH) 동시 반영(AuthContext)
    switchReq?.resolve?.(!!role);
    setSwitchReq(null);
  };
  const cancelSwitch = () => {
    switchReq?.resolve?.(false);
    setSwitchReq(null);
  };
  const leavePosting = () => {
    switchReq?.resolve?.(false);
    setSwitchReq(null);
    setRoleReq(null);
    navigate(-1);
  };
  const goAddRole = () => {
    switchReq?.resolve?.(false);
    setSwitchReq(null);
    setRoleReq(null);
    openMemberType(navigate, activeType);
  };
  const switchCopy = roleGateCopy({
    url: switchReq?.body?.request?.url,
    activeType,
    suggested,
    message: switchReq?.body?.message,
  });
  const roleCopy = roleGateCopy({
    url: roleReq?.body?.request?.url,
    activeType,
    message: roleReq?.body?.message,
  });
  // ★ 2026-10-19 이미 추가(인증 신청)했지만 승인 전인 유형 — 서버 ROLE_REQUIRED data.verification.
  //  '유형을 추가하세요'를 다시 띄우면 추가했는데 또 묻는 것처럼 보인다 → 심사 중/인증 필요로 안내한다.
  const verification = roleReq?.body?.data?.verification || {};
  const verifyEntries = Object.entries(verification);
  const pendingRole = verifyEntries.find(([, st]) => st === "PENDING")?.[0];
  const unverifiedRole = verifyEntries.find(([, st]) => st !== "PENDING")?.[0];
  const verifyNotice = pendingRole
    ? { text: `${ROLE_LABELS[pendingRole] || pendingRole} 인증을 심사하고 있어요. 승인되면 바로 이용할 수 있어요.`, button: "인증 상태 보기" }
    : unverifiedRole
      ? { text: `${ROLE_LABELS[unverifiedRole] || unverifiedRole} 유형은 서류 인증을 마치면 이용할 수 있어요.`, button: "인증하러 가기" }
      : null;
  const gateUrl = switchReq?.body?.request?.url || roleReq?.body?.request?.url || "";
  const isPostingFlow = /^\/(?:muyeon\/)?(jobs|subs|casting)(\/|$)/.test(location.pathname)
    || /\/(jobs|subs|casting)(\/|$)/.test(gateUrl);

  return (
    <>
      {isPostingFlow ? <>
        <RoleChangeModal isShow={!!switchReq} title={switchCopy.title} description={switchCopy.description}
          roles={switchRoles} onSelect={confirmSwitch} onAdd={goAddRole} onBack={leavePosting} />
        <RoleChangeModal isShow={!!roleReq} title={roleCopy.title}
          description={verifyNotice ? verifyNotice.text : roleCopy.description}
          roles={[]} onAdd={goAddRole} onBack={leavePosting} addLabel={verifyNotice?.button} />
      </> : <>
        <div data-id="switch-required" className={`modal-dimmed ${switchReq ? "is-show" : ""}`}>
          <div className="modal">
            <button type="button" className="ico-btn" onClick={cancelSwitch}>
              <i className="close"></i>
              <span className="visually-hidden">닫기</span>
            </button>
            <div className="tit-box"><p>{switchReq?.body?.message || "다른 회원유형에서 이용할 수 있는 기능이에요."}</p></div>
            <div className="btn-group">
              <button type="button" className="btn-md filled primary" onClick={() => confirmSwitch(suggested)}>
                {suggested ? `${ROLE_LABELS[suggested] || suggested} 유형으로 전환하고 계속하기` : "전환하기"}
              </button>
            </div>
          </div>
        </div>
        <div data-id="role-required" className={`modal-dimmed ${roleReq ? "is-show" : ""}`}>
          <div className="modal">
            <div className="tit-box"><p>{verifyNotice ? verifyNotice.text : (roleReq?.body?.message || "지금 회원유형으로는 이용할 수 없어요.") + " MY > 회원유형 설정에서 유형을 추가해 주세요."}</p></div>
            <div className="btn-group">
              <button type="button" className="btn-md outlined" onClick={() => setRoleReq(null)}>닫기</button>
              <button type="button" className="btn-md filled primary" onClick={goAddRole}>{verifyNotice ? verifyNotice.button : "회원유형 추가하기"}</button>
            </div>
          </div>
        </div>
      </>}
    </>
  );
}
